import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useLenis } from "lenis/react";
import { Menu, X, MessageCircle, Check, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const { lang, changeLang, t, langs } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el && lenis) lenis.scrollTo(el, { offset: -80 });
      else if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const links = [
    { id: "solutions", label: t.nav.solutions },
    { id: "configurator", label: t.nav.configurator },
    { id: "partner", label: t.nav.partner },
    { id: "manifesto", label: t.nav.manifesto },
    { id: "faq", label: t.nav.faq },
    { id: "blog", label: t.nav.blog, to: "/blog" },
    { id: "contact", label: t.nav.contact },
  ];

  const handleLink = (l) => {
    if (l.to) navigate(l.to);
    else goTo(l.id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="relative mx-auto flex max-w-[1500px] items-center justify-between px-5 py-4 md:px-10">
        {/* Logo */}
        <Link
          to="/"
          onClick={() => lenis && lenis.scrollTo(0, { immediate: false })}
          className="z-10 flex items-baseline gap-0.5"
          data-testid="logo-home-button"
        >
          <span className="font-display text-2xl font-black tracking-tight text-white">
            TUNINGFILES
          </span>
          <span className="font-mono text-sm text-brand-green">.hu</span>
        </Link>

        {/* Centered nav */}
        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-6 xl:flex">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => handleLink(l)}
              className="group relative font-mono text-[11px] uppercase tracking-[0.16em] text-white/70 transition-colors hover:text-white"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-brand-green transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        {/* Right controls */}
        <div className="z-10 flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1 border border-white/15 px-3 py-2 font-mono text-xs uppercase tracking-widest text-white/80 transition-colors hover:border-brand-green hover:text-white"
                data-testid="lang-switcher-trigger"
              >
                {lang} <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[160px] border-white/10 bg-[#121212] text-white"
            >
              {langs.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => changeLang(l.code)}
                  className="cursor-pointer font-mono text-xs uppercase tracking-widest focus:bg-white/10 focus:text-brand-green"
                  data-testid={`lang-option-${l.code}`}
                >
                  <span className="flex w-full items-center justify-between">
                    {l.name}
                    {lang === l.code && <Check className="h-3 w-3 text-brand-green" />}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            href={`https://wa.me/${CONTACT.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 bg-brand-green px-5 py-2.5 font-mono text-xs font-medium uppercase tracking-widest text-black transition-transform hover:-translate-y-0.5 hover:shadow-glow-green md:flex"
            data-testid="header-cta-whatsapp"
          >
            <MessageCircle className="h-4 w-4" />
            {t.nav.cta}
          </a>

          <button
            onClick={() => setOpen(true)}
            className="text-white xl:hidden"
            data-testid="mobile-menu-open"
            aria-label="Menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col bg-[#0A0A0A] px-6 py-6 xl:hidden"
            data-testid="mobile-menu-panel"
          >
            <div className="flex items-center justify-between">
              <span className="font-display text-2xl font-black text-white">
                TUNINGFILES<span className="text-brand-green">.hu</span>
              </span>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close">
                <X className="h-7 w-7 text-white" />
              </button>
            </div>
            <nav className="mt-16 flex flex-col gap-7">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => {
                    setOpen(false);
                    setTimeout(() => handleLink(l), 250);
                  }}
                  className="text-left font-display text-4xl font-bold text-white"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-auto flex items-center justify-center gap-2 bg-brand-green py-4 font-mono text-sm font-medium uppercase tracking-widest text-black"
              data-testid="mobile-cta-whatsapp"
            >
              <MessageCircle className="h-5 w-5" />
              {t.nav.cta}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
